import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import grayBg from 'assets/img/solid-bg-gray.png';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Button,
  Card,
  Carousel,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import InfoRealEstateModal from '../../../gestionarPropiedades/tables/propertiesTable/components/InfoRealEstateModal';
import SharePropModal from '../../../gestionarPropiedades/tables/propertiesTable/components/SharePropModal';
import { TitleGenerator } from 'utils/TitleGenerator';

const currObj = {
  1: 'USD',
  2: 'ARS'
};

const CharacteristicItem = ({ icon, number, text }) => (
  <div className="d-flex flex-column align-items-center justify-content-center gap-2">
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip className="tooltip-custom">
          <strong>{text}</strong>
        </Tooltip>
      }
    >
      <span
        className="d-flex justify-content-center align-items-center gap-2 ps-2"
        style={{ color: '#344050' }}
      >
        <p className="m-0">
          <strong>{number}</strong>
        </p>
        <FontAwesomeIcon icon={icon} />
      </span>
    </OverlayTrigger>
  </div>
);

CharacteristicItem.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  number: PropTypes.number.isRequired
};

const SharedCard = ({
  proj,
  loading,
  noShareButton,
  noRounded,
  noShareModal,
  noShadow,
  selected,
  setSelected
}) => {
  const [showControls, setShowControls] = useState(false);
  const [openInfoRealEstateModal, setOpenInfoRealEstateModal] = useState(false);
  const [propToShare, setPropToShare] = useState(null);

  const price = proj.info?.price;
  const surface = proj.info?.surface;
  const ambiences = proj.info?.ambience;
  const bathrooms = proj.info?.bathrooms;

  const handleLogoUrl = () => {
    if (proj.real_estate.logo) {
      return proj.real_estate.logo.match(/http/)
        ? proj.real_estate.logo
        : `${process.env.REACT_APP_CLIENT}/uploads/realestate/logo/${proj.real_estate.logo}`;
    }
  };

  const HandleCheckbox = () => {
    if (selected.find(project => project.id === proj.id)) {
      setSelected(selected.filter(project => project.id !== proj.id));
    } else {
      setSelected([
        ...selected,
        { id: proj.id, title: proj.title, status: proj.status }
      ]);
    }
  };

  return (
    <>
      <Card
        className={classNames('bg-white p-0 h-100', {
          'rounded-0': noRounded,
          'shadow-sm': noShadow
        })}
      >
        <div className="position-relative">
          <div
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              zIndex: 2,
              background: '#fff',
              width: 30,
              height: 30,
              borderRadius: 5,
              top: 5,
              right: 5
            }}
          >
            <Form.Check
              type="checkbox"
              onChange={HandleCheckbox}
              name={proj.id}
              checked={
                selected
                  ? selected.find(project => project.id === proj.id)
                  : false
              }
            />
          </div>
          <Carousel
            interval={null}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            controls={showControls && proj.images.length > 1}
            indicators={showControls}
            className="theme-slider"
            nextIcon={
              <FontAwesomeIcon
                icon="circle-arrow-right"
                size="2xl"
                className="border-icon"
              />
            }
            prevIcon={
              <FontAwesomeIcon
                icon="circle-arrow-left"
                size="2xl"
                className="border-icon"
              />
            }
          >
            {!proj.images ? (
              <Carousel.Item>
                <Card.Img
                  src={grayBg}
                  alt={proj.title}
                  style={{
                    aspectRatio: '16/9',
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    objectFit: 'cover'
                  }}
                  className={classNames({
                    'rounded-0': noRounded
                  })}
                />
              </Carousel.Item>
            ) : (
              proj.images.map(image => (
                <Carousel.Item
                  key={
                    image.url ||
                    `${process.env.REACT_APP_CLIENT}/uploads/projects/${image.filename}`
                  }
                >
                  <Card.Img
                    src={
                      image.url ||
                      `${process.env.REACT_APP_CLIENT}/uploads/projects/${image.filename}`
                    }
                    alt={proj.title}
                    style={{
                      aspectRatio: '16/9',
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                      objectFit: 'cover'
                    }}
                    className={classNames({
                      'rounded-0': noRounded
                    })}
                  />
                </Carousel.Item>
              ))
            )}
          </Carousel>

          {proj.real_estate && (
            <img
              src={handleLogoUrl() || grayBg}
              className="real-estate-logo"
              onClick={() => setOpenInfoRealEstateModal(true)}
            />
          )}
        </div>

        <Card.Header>
          <Card.Subtitle className="mb-2">
            <p className="m-0 text-muted">Emprendimiento - {proj.type}</p>
          </Card.Subtitle>
          <Card.Title>{proj.title}</Card.Title>

          <Card.Text className="m-0">
            {proj.address}, <strong>{proj.barrios}</strong>, CABA
          </Card.Text>
        </Card.Header>

        <Card.Body className="py-1">
          <div className="d-flex flex-column justify-content-end h-100">
            <div className="d-flex flex-wrap gap-2 justify-content-start">
              <CharacteristicItem
                icon="house"
                number={proj.units?.length || '-'}
                text="Unidades"
              />

              <CharacteristicItem
                icon="door-closed"
                number={`${
                  ambiences?.min === ambiences?.max
                    ? ambiences?.min
                    : `${ambiences?.min} - ${ambiences?.max}`
                }`}
                text="Ambientes"
              />

              <CharacteristicItem
                icon="bath"
                number={
                  bathrooms?.min === bathrooms?.max
                    ? bathrooms?.min
                    : `${bathrooms?.min} - ${bathrooms?.max}`
                }
                text="Baños"
              />

              <CharacteristicItem
                icon="ruler-combined"
                number={
                  surface?.min === surface?.max
                    ? surface?.min
                    : `${surface?.min} - ${surface?.max}`
                }
                text="Superficie Total en m²"
              />
            </div>

            <p className="m-0 mt-3 small">Publicado el {proj.created_at}</p>
          </div>
        </Card.Body>

        <style>
          @media (max-width: 575.98px){' '}
          {`
            .d-flex.gap-2.justify-content-start.justify-content-sm-between > div {
                flex-basis: calc(50% - 1rem); /* 1rem es aproximadamente el tamaño del gap, ajústalo según tus necesidades */
                max-width: calc(50% - 1rem);
            }
          `}
        </style>

        <Card.Footer className="border-top">
          <Row>
            <Col
              xs={12}
              sm={8}
              md={7}
              lg={!noShareButton && proj.share.percent ? 7 : 12}
            >
              <p
                className={classNames('m-0 fw-bold fs-1', {
                  'mb-1': loading
                })}
              >
                <span className="fw-normal fs-0">Desde</span>{' '}
                {currObj[price?.min.currency]} {price?.min.amount}
              </p>

              <p
                className={classNames('m-0 fw-bold fs-1', {
                  'mb-1': loading
                })}
              >
                <span className="fw-normal fs-0">Hasta</span>{' '}
                {currObj[price?.max.currency]} {price?.max.amount}
              </p>
            </Col>

            {!noShareButton && proj.share.percent && (
              <Col
                xs={12}
                sm={4}
                md={5}
                lg={5}
                className="text-end d-flex align-items-center justify-content-end"
              >
                <div>
                  <p
                    className={classNames('m-0 fs--1', {
                      'mb-1': loading
                    })}
                  >
                    Honorarios:{' '}
                    <span className="m-0 fw-bold fs-1">
                      {proj.share.percent}%
                    </span>
                  </p>
                  <p className="m-0 fw-bold fs-1">{proj.share.percent}%</p>
                </div>
              </Col>
            )}
          </Row>

          <div className="mt-3 d-flex flex-column gap-2 w-100 mt-sm-4 mt-lg-2">
            {!noShareButton && (
              <Button
                className="w-100 w-sm-auto"
                style={{ flexGrow: 1 }}
                size="sm"
                variant="primary"
                onClick={() => setPropToShare(proj)}
              >
                Compartir
              </Button>
            )}
            <Button
              className="w-100 w-sm-auto"
              style={{ flexGrow: 1 }}
              size="sm"
              variant="secondary"
              key={`view-${proj._id}`}
              onClick={() =>
                window.open(
                  `${process.env.REACT_APP_CLIENT_DETAIL_PROJECT}${
                    proj.id
                  }/${TitleGenerator(proj.title)}`,
                  '_blank'
                )
              }
              tooltipText="Ver en CabaProp"
              tooltipPosition="left"
            >
              Ver en Cabaprop
            </Button>
          </div>
        </Card.Footer>
      </Card>

      {proj.real_estate && (
        <InfoRealEstateModal
          show={openInfoRealEstateModal}
          setShow={setOpenInfoRealEstateModal}
          realEstate={proj.real_estate}
          branchOffice={proj.branch_office}
        />
      )}

      {!noShareModal && (
        <SharePropModal
          propToShare={propToShare}
          setPropToShare={setPropToShare}
          type="project"
        />
      )}
    </>
  );
};

SharedCard.propTypes = {
  proj: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  noShareButton: PropTypes.bool,
  noRounded: PropTypes.bool,
  noShareModal: PropTypes.bool,
  noShadow: PropTypes.bool,
  noSharedPercent: PropTypes.bool,
  selected: PropTypes.array,
  setSelected: PropTypes.func
};

export default SharedCard;
